/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import SheetRenderer from "ui/feature/piece-play/common/sheet-renderer/SheetRenderer";
import {
    OnSheetNoteByNotePlayer_Piece$data
} from "ui/feature/piece-play/on-sheet-note-by-note/__generated__/OnSheetNoteByNotePlayer_Piece.graphql";
import {BulbIdToStateLookup} from "ui/feature/piece-play/common/PlayTypes";
import PlayType from "ui/feature/piece-play/common/PlayType";
import MIDI, {MIDIEvent} from "state/midi";

interface Props {
    piece: OnSheetNoteByNotePlayer_Piece$data;
}

const OnSheetNoteByNotePlayer: React.FC<Props> = ({piece}) => {
    const [, setFinished] = React.useState(false);
    const midiContext = React.useContext(MIDI)
    const [currStartTime, setCurrStartTime] = React.useState<number>(0)
    const [bulbIdToStateLookup, setBulbIdToStateLookup] = React.useState<BulbIdToStateLookup>({});
    const [playType, ] = React.useState<PlayType>("both_hands")
    const [currentPressedNotes, setCurrentPressedNotes] = React.useState<{[key: number]: boolean}>([]);

    const currMidiValues: Array<{ midiNumber: number, bulbId: string }> = React.useMemo(() => {
        const file = piece.file;
        const output = new Array<{ midiNumber: number, bulbId: string }>();
        if (file) {
            for (const note of file.notes) {
                if (playType === "left_hand" && !note.left_handed) {
                    continue;
                } else if (playType === "right_hand" && note.left_handed) {
                    continue;
                }
                if (note.note_start === currStartTime) {
                    output.push({
                        midiNumber: note.midi_number,
                        bulbId: note.bulb_id
                    });
                }
            }
        }
        return output;
    }, [currStartTime, piece.file, playType]);

    const handleMIDIEvent = React.useCallback((e: MIDIEvent) => {
        if (e.event_type === "key_down") {
            setCurrentPressedNotes((prev) => {
                return {
                    ...prev,
                    [e.key]: true
                }
            });
        } else if (e.event_type === "key_up") {
            setCurrentPressedNotes((prev) => {
                return {
                    ...prev,
                    [e.key]: false
                }
            });
        }
    }, []);

    const nextPlayPosition = React.useMemo(() => {
        let minStartTime = null;
        for (const note of piece.file?.notes || []) {
            if (note.note_start > currStartTime) {
                if (minStartTime === null || note.note_start < currStartTime) {
                    minStartTime = note.note_start;
                }
            }
        }
        return minStartTime;
    }, [currStartTime, piece.file?.notes]);

    // check if needs to proceed
    React.useEffect(() => {
        let neededMidiValues = currMidiValues.map((elem) => elem.midiNumber)
        let playedMidiValues = []
        for (const midiString in currentPressedNotes) {
            const midi = parseInt(midiString)
            if (currentPressedNotes[midi]) {
                playedMidiValues.push(midi)
            }
        }
        if (neededMidiValues.length !== playedMidiValues.length) {
            return;
        }
        neededMidiValues = neededMidiValues.sort()
        playedMidiValues = playedMidiValues.sort()

        for (let i = 0; i < neededMidiValues.length; i += 1) {
            if (neededMidiValues[i] !== playedMidiValues[i]) {
                return;
            }
        }

        if (nextPlayPosition) {
            // we need to go to the next position
            setCurrentPressedNotes({}); // reset the pressed notes, to avoid issues with two notes being played
            setCurrStartTime(nextPlayPosition);
        } else {
            // no next position, we are finished
            setFinished(true);
        }

    }, [nextPlayPosition, currStartTime, currentPressedNotes, currMidiValues]);

    const setOnMIDIEvent = midiContext.setOnMIDIEvent;

    React.useEffect(() => {
        setOnMIDIEvent(() => handleMIDIEvent);
    }, [handleMIDIEvent, setOnMIDIEvent])

    React.useEffect(() => {
        // update the bulb state lookup with the current midi values
        console.log(currentPressedNotes);
        setBulbIdToStateLookup((oldValue) => ({
            ...oldValue,
            ...Object.assign({}, ...currMidiValues.map((val) => {
                return {
                    [val.bulbId]: currentPressedNotes[val.midiNumber] ? "success" : "current"
                };
            }))
        }));
    }, [currMidiValues, currentPressedNotes]);

    return (
        <SheetRenderer bulbIdToStateLookup={bulbIdToStateLookup} playPosition={0} piece={piece} />
    );
}

export default createFragmentContainer(OnSheetNoteByNotePlayer, {
    piece: graphql`
    fragment OnSheetNoteByNotePlayer_Piece on MusicPiece {
        id
        file {
            notes {
                bulb_id
                midi_number
                left_handed
                note_start
            }
        }
        ...SheetRenderer_piece
    }
    `
});